// EM²c command/control network.
// Command Center = 6 hex radius, Command Complex = 10, Relay Tower = 8.
// Command Center and Command Complex are independent signal roots. Relay Towers
// MUST be placed inside already-active coverage and then extend the network.
// If the command path is broken, affected buildings and construction sites stop
// until connectivity is restored.

import { useGame } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { hexDistance, rotateFootprint } from "../hexgrid";
import { PLACEMENT } from "../hexplacement";

export const COMMAND_CENTER_RADIUS = 6;
export const COMMAND_COMPLEX_RADIUS = 10;
export const RELAY_TOWER_RADIUS = 8;

const ROOT_TYPES = new Set(["command_center", "mega_command"]);
const RELAY_TYPE = "relay_tower";
let intervalId = null;
let installed = false;
let basePlacementValidator = null;
let selectionUnsubscribe = null;
let selectionGridUpdateInProgress = false;
let selectionGridForced = false;
let selectionPreviousGridVisible = false;
const heldConstructionProgress = new Map();

export function isCommandNodeType(type) {
  return ROOT_TYPES.has(type) || type === RELAY_TYPE;
}

export function getCommandNodeRadius(building) {
  if (!building) return 0;
  if (building.type === "command_center") return COMMAND_CENTER_RADIUS;
  if (building.type === "mega_command") return COMMAND_COMPLEX_RADIUS;
  if (building.type === RELAY_TYPE) return RELAY_TOWER_RADIUS;
  return Number(EM2C_BUILDINGS[building.type]?.controlRadius || 0);
}

function playerAllowsOperation(building) {
  return building.active !== false || building.networkAutoDisabled === true;
}

function nodeUsable(building) {
  // Damage must NOT silently delete command coverage. A damaged Command Center,
  // Command Complex or Relay Tower still carries signal while it physically
  // exists and has condition remaining.
  return !!building &&
    Number(building.progress || 0) >= 1 &&
    Number(building.condition ?? 1) > 0 &&
    playerAllowsOperation(building);
}

export function getConnectedCommandNodes(state, excludeBuildingId = null) {
  const buildings = (state?.buildings || []).filter((b) => b.id !== excludeBuildingId);
  const roots = buildings.filter((b) => ROOT_TYPES.has(b.type) && nodeUsable(b));
  const connected = new Map(roots.map((b) => [b.id, b]));
  const remaining = buildings.filter((b) => b.type === RELAY_TYPE && nodeUsable(b));

  let changed = true;
  while (changed) {
    changed = false;
    for (const relay of remaining) {
      if (connected.has(relay.id)) continue;
      const touchesNetwork = [...connected.values()].some((node) =>
        hexDistance(node.q, node.r, relay.q, relay.r) <= getCommandNodeRadius(node)
      );
      if (touchesNetwork) {
        connected.set(relay.id, relay);
        changed = true;
      }
    }
  }
  return connected;
}

export function getHexesInCommandNodeCoverage(grid, building) {
  if (!grid || !building) return [];
  const radius = getCommandNodeRadius(building);
  if (!(radius > 0)) return [];
  return Array.from(grid.values()).filter((hex) =>
    hexDistance(building.q, building.r, hex.q, hex.r) <= radius
  );
}

export function getActiveCommandCoverageHexes(state, grid = state?.grid, excludeBuildingId = null) {
  if (!grid) return [];
  const nodes = [...getConnectedCommandNodes(state, excludeBuildingId).values()];
  if (!nodes.length) return [];
  return Array.from(grid.values()).filter((hex) =>
    nodes.some((node) => hexDistance(node.q, node.r, hex.q, hex.r) <= getCommandNodeRadius(node))
  );
}

export function isHexInCommandCoverage(state, q, r, excludeBuildingId = null) {
  const nodes = getConnectedCommandNodes(state, excludeBuildingId);
  for (const node of nodes.values()) {
    if (hexDistance(node.q, node.r, q, r) <= getCommandNodeRadius(node)) return true;
  }
  return false;
}

export function isFootprintInCommandCoverage(state, type, q, r, rotation = 0, excludeBuildingId = null) {
  const def = EM2C_BUILDINGS[type];
  if (!def) return false;
  const footprint = rotateFootprint(def.footprintHexes || [{ q: 0, r: 0 }], rotation || 0);
  return footprint.every((cell) => isHexInCommandCoverage(state, q + cell.q, r + cell.r, excludeBuildingId));
}

export function validateCommandNetworkPlacement(state, type, q, r, rotation = 0, excludeBuildingId = null) {
  if (type === "command_center") {
    const anotherCommandCenter = (state?.buildings || []).some(
      (building) => building.type === "command_center" && building.id !== excludeBuildingId
    );
    if (anotherCommandCenter) {
      return { valid: false, reason: "Only one Command Center can be built", placement: PLACEMENT.BLOCKED };
    }
    return { valid: true };
  }

  // Command Complex is a full command root, not a relay. It can be built or
  // moved outside existing coverage and creates its own 10-hex signal once live.
  if (type === "mega_command") return { valid: true };

  if (type === RELAY_TYPE) {
    if (!isHexInCommandCoverage(state, q, r, excludeBuildingId)) {
      return { valid: false, reason: "Relay Tower must be inside existing active command coverage", placement: PLACEMENT.BLOCKED };
    }
    return { valid: true };
  }

  if (!isFootprintInCommandCoverage(state, type, q, r, rotation, excludeBuildingId)) {
    return { valid: false, reason: "Outside active drone command coverage — extend the network with a Relay Tower", placement: PLACEMENT.BLOCKED };
  }
  return { valid: true };
}

function buildingConnected(state, building, connectedNodes) {
  if (ROOT_TYPES.has(building.type)) return nodeUsable(building);
  if (building.type === RELAY_TYPE) return connectedNodes.has(building.id);
  const def = EM2C_BUILDINGS[building.type];
  if (!def) return false;
  const footprint = rotateFootprint(def.footprintHexes || [{ q: 0, r: 0 }], building.rotation || 0);
  return footprint.every((cell) => {
    const q = building.q + cell.q;
    const r = building.r + cell.r;
    for (const node of connectedNodes.values()) {
      if (hexDistance(node.q, node.r, q, r) <= getCommandNodeRadius(node)) return true;
    }
    return false;
  });
}

function constructionSiteConnected(state, building) {
  // Both command roots may bootstrap their own coverage. Their construction
  // does not require another Command Center/Complex or a Relay Tower nearby.
  if (ROOT_TYPES.has(building.type)) return true;
  if (building.type === RELAY_TYPE) return isHexInCommandCoverage(state, building.q, building.r, building.id);
  return isFootprintInCommandCoverage(state, building.type, building.q, building.r, building.rotation || 0, building.id);
}

function reconcileSelectedNodeGridVisibility(state) {
  if (selectionGridUpdateInProgress) return;
  const selected = (state.buildings || []).find((building) => building.id === state.selectedId) || null;
  const shouldForce = !!selected && isCommandNodeType(selected.type) && !state.placementType && !state.moveBuildingId;

  if (shouldForce) {
    if (!selectionGridForced) {
      selectionGridForced = true;
      selectionPreviousGridVisible = Boolean(state.gridVisible);
    }
    if (!state.gridVisible) {
      selectionGridUpdateInProgress = true;
      try { useGame.setState({ gridVisible: true }); }
      finally { selectionGridUpdateInProgress = false; }
    }
    return;
  }

  if (!selectionGridForced) return;
  const restoreNow = !state.placementType && !state.moveBuildingId;
  const restoreValue = selectionPreviousGridVisible;
  selectionGridForced = false;

  if (restoreNow && Boolean(state.gridVisible) !== restoreValue) {
    selectionGridUpdateInProgress = true;
    try { useGame.setState({ gridVisible: restoreValue }); }
    finally { selectionGridUpdateInProgress = false; }
  }
}

function reconcileNetworkState() {
  const state = useGame.getState();
  const connectedNodes = getConnectedCommandNodes(state);
  let buildingsChanged = false;

  const liveIds = new Set((state.buildings || []).map((building) => building.id));
  for (const id of [...heldConstructionProgress.keys()]) {
    if (!liveIds.has(id)) heldConstructionProgress.delete(id);
  }

  const nextBuildings = (state.buildings || []).map((building) => {
    if (Number(building.progress || 0) < 1) {
      const connected = constructionSiteConnected(state, building);
      if (!connected) {
        const held = heldConstructionProgress.has(building.id)
          ? heldConstructionProgress.get(building.id)
          : Number(building.progress || 0);
        heldConstructionProgress.set(building.id, held);
        if (building.progress !== held || building.state !== "disconnected" || !building.networkAutoDisabled) buildingsChanged = true;
        return {
          ...building,
          progress: held,
          active: false,
          networkAutoDisabled: true,
          networkConnected: false,
          state: "disconnected",
          controllerStatus: "disconnected",
          controllerReason: "Construction paused — no command network connection",
        };
      }

      heldConstructionProgress.delete(building.id);
      if (building.networkAutoDisabled || building.networkConnected !== true) {
        buildingsChanged = true;
        return {
          ...building,
          active: true,
          networkAutoDisabled: false,
          networkConnected: true,
          state: "under_construction",
          controllerStatus: "under_construction",
          controllerReason: null,
        };
      }
      return building;
    }

    heldConstructionProgress.delete(building.id);
    const connected = buildingConnected(state, building, connectedNodes);

    if (!connected && !ROOT_TYPES.has(building.type)) {
      if (building.networkAutoDisabled && building.networkConnected === false && building.state === "disconnected") return building;
      buildingsChanged = true;
      return {
        ...building,
        active: false,
        networkAutoDisabled: building.active !== false || building.networkAutoDisabled === true,
        networkConnected: false,
        state: "disconnected",
        controllerStatus: "disconnected",
        controllerReason: "No command network connection",
      };
    }

    if (connected) {
      if (building.networkAutoDisabled) {
        buildingsChanged = true;
        return {
          ...building,
          active: true,
          networkAutoDisabled: false,
          networkConnected: true,
          state: "operational",
          controllerStatus: "operational",
          controllerReason: null,
        };
      }
      if (building.networkConnected !== true) {
        buildingsChanged = true;
        return { ...building, networkConnected: true };
      }
    }
    return building;
  });

  const connectedIds = new Set(nextBuildings.filter((b) => b.networkConnected !== false).map((b) => b.id));
  let dronesChanged = false;
  const nextDrones = (state.drones || []).map((drone) => {
    if (!drone.target || connectedIds.has(drone.target)) return drone;
    dronesChanged = true;
    return { ...drone, task: "idle", target: null, carry: 0 };
  });

  if (buildingsChanged || dronesChanged) {
    useGame.setState({
      buildings: buildingsChanged ? nextBuildings : state.buildings,
      drones: dronesChanged ? nextDrones : state.drones,
    });
  }
}

export function installCommandNetworkIntegration() {
  if (installed) return () => {};
  installed = true;

  const state = useGame.getState();
  basePlacementValidator = state.isValidHexPlacement;
  useGame.setState({
    isValidHexPlacement: (type, q, r, rotation) => {
      const current = useGame.getState();
      const baseResult = basePlacementValidator?.(type, q, r, rotation) || { valid: true };
      if (!baseResult.valid) return baseResult;
      const networkResult = validateCommandNetworkPlacement(current, type, q, r, rotation);
      return networkResult.valid ? baseResult : networkResult;
    },

    getCommandNetwork: () => {
      const current = useGame.getState();
      return {
        nodes: [...getConnectedCommandNodes(current).values()],
        commandCenterRadius: COMMAND_CENTER_RADIUS,
        commandComplexRadius: COMMAND_COMPLEX_RADIUS,
        relayRadius: RELAY_TOWER_RADIUS,
      };
    },

    isHexInCommandCoverage: (q, r) => isHexInCommandCoverage(useGame.getState(), q, r),
  });

  reconcileNetworkState();
  reconcileSelectedNodeGridVisibility(useGame.getState());
  selectionUnsubscribe = useGame.subscribe((next) => reconcileSelectedNodeGridVisibility(next));
  if (typeof window !== "undefined") intervalId = window.setInterval(reconcileNetworkState, 150);
  return () => {
    if (intervalId) window.clearInterval(intervalId);
    intervalId = null;
    selectionUnsubscribe?.();
    selectionUnsubscribe = null;
    heldConstructionProgress.clear();
    selectionGridForced = false;
    selectionPreviousGridVisible = false;
    selectionGridUpdateInProgress = false;
    installed = false;
  };
}
